import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OrganizationDetailsData } from '../../pages/SetupWizardPage'

interface Step1OrganizationDetailsProps {
  initialData: OrganizationDetailsData | null
  onSubmit: (data: OrganizationDetailsData) => void
  onCancel: () => void
  loading: boolean
}

const ORGANIZATION_TYPES = [
  { value: 'association', label: 'Yhdistys', description: 'Rekisteröity yhdistys' },
  { value: 'company', label: 'Yritys', description: 'Osakeyhtiö tai toiminimi' },
  { value: 'foundation', label: 'Säätiö', description: 'Rekisteröity säätiö' },
  { value: 'other', label: 'Muu', description: 'Muu organisaatio' },
] as const

export default function Step1OrganizationDetails({
  initialData,
  onSubmit,
  onCancel,
  loading,
}: Step1OrganizationDetailsProps) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<OrganizationDetailsData>({
    name: initialData?.name || '',
    business_id: initialData?.business_id || '',
    address: initialData?.address || '',
    postal_code: initialData?.postal_code || '',
    city: initialData?.city || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    website: initialData?.website || '',
    type: initialData?.type || 'association',
    description: initialData?.description || '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Nimi on pakollinen'
    }

    if (formData.business_id && !/^\d{7}-\d$/.test(formData.business_id)) {
      newErrors.business_id = 'Y-tunnuksen muoto on virheellinen (esim. 1234567-8)'
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.invalidEmail')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({
      name: true,
      business_id: true,
      email: true,
    })

    if (validate()) {
      onSubmit(formData)
    }
  }

  const handleChange = (field: keyof OrganizationDetailsData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setTouched(prev => ({ ...prev, [field]: true }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Organisaation perustiedot</h2>
      <p className="text-gray-600 mb-6">Anna yhdistyksesi perustiedot. Voit täydentää näitä myöhemmin asetuksista.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Organisaation nimi <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Esimerkki yhdistys ry"
            className={`
              w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
              ${errors.name && touched.name
                ? 'border-red-300 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
              }
            `}
          />
          {errors.name && touched.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Organization type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Organisaation tyyppi</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ORGANIZATION_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleChange('type', type.value)}
                className={`
                  p-3 border-2 rounded-lg text-left transition-colors
                  ${formData.type === type.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                  }
                `}
              >
                <div className="font-medium text-sm text-gray-900">{type.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Business ID */}
        <div>
          <label htmlFor="business_id" className="block text-sm font-medium text-gray-700 mb-1">
            Y-tunnus
          </label>
          <input
            type="text"
            id="business_id"
            value={formData.business_id}
            onChange={(e) => handleChange('business_id', e.target.value)}
            placeholder="1234567-8"
            className={`
              w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
              ${errors.business_id && touched.business_id
                ? 'border-red-300 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
              }
            `}
          />
          {errors.business_id && touched.business_id && (
            <p className="mt-1 text-sm text-red-600">{errors.business_id}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">Valinnainen. Muoto: 1234567-8</p>
        </div>

        {/* Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Katuosoite
            </label>
            <input
              type="text"
              id="address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Esimerkinkatu 1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
              Postinumero
            </label>
            <input
              type="text"
              id="postal_code"
              value={formData.postal_code}
              onChange={(e) => handleChange('postal_code', e.target.value)}
              placeholder="00100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              Postitoimipaikka
            </label>
            <input
              type="text"
              id="city"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Helsinki"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
        </div>

        {/* Contact info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Sähköposti
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="email@example.com"
              className={`
                w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2
                ${errors.email && touched.email
                  ? 'border-red-300 focus:ring-red-200'
                  : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
                }
              `}
            />
            {errors.email && touched.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Puhelin
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+358 40 123 4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
        </div>

        {/* Website */}
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
            Verkkosivut
          </label>
          <input
            type="url"
            id="website"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="https://www.example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Kuvaus
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Lyhyt kuvaus yhdistyksen toiminnasta"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Peruuta
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Tallennetaan...
              </>
            ) : (
              <>Seuraava →</>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
